import React, { useState } from "react"

import { CheckCircle2, ChevronDown, ChevronRight, FileText, XCircle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"

import type { Tables } from "../../../database.types"

export interface JobPostingStage {
  id: string
  meta?: Record<string, string | number>
  segments: JobPostingSegment[]
}

export interface JobPostingSegment {
  id: string
  [key: string]: string
}

interface DocumentsListProps {
  jobStages: JobPostingStage[]
  documents: Tables<"documents">[]
  onViewDocument: (document: Tables<"documents">) => void
}

const DocumentsList = ({ jobStages, documents, onViewDocument }: DocumentsListProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const allSegments = jobStages.flatMap((stage) => stage.segments).map((segment) => segment.id)
  // Define all required document types
  const allDocuments = [
    {
      id: "dvla-front",
      displayName: "DVLA License (Front)",
      required: allSegments.includes("dvla-check"),
    },
    {
      id: "dvla-back",
      displayName: "DVLA License (Back)",
      required: allSegments.includes("dvla-check"),
    },
    {
      id: "insurance-proof",
      displayName: "Insurance Proof",
      required: allSegments.includes("insurance-check"),
    },
    {
      id: "rtw-document",
      displayName: "Right to Work Document",
      required: allSegments.includes("rtw-check"),
    },
    {
      id: "bank-proof",
      displayName: "Bank Proof",
      required: allSegments.includes("bank-details"),
    },
    {
      id: "headshot",
      displayName: "Headshot Photo",
      required: allSegments.includes("headshot"),
    },
    {
      id: "vehicle-release-form",
      displayName: "Vehicle Release Form",
      required: allSegments.includes("vehicle-release-form"),
    },
    {
      id: "driver-contract",
      displayName: "Driver Contract",
      required: jobStages.some((stage) => stage.id === "contract"),
    },
    {
      id: "tacho-card-front",
      displayName: "Tacho Card (Front)",
      required: allSegments.includes("tacho-card-upload"),
    },
    {
      id: "tacho-card-back",
      displayName: "Tacho Card (Back)",
      required: allSegments.includes("tacho-card-upload"),
    },
    {
      id: "cpc-front",
      displayName: "CPC Card (Front)",
      required: allSegments.includes("cpc-upload"),
    },
    {
      id: "cpc-back",
      displayName: "CPC Card (Back)",
      required: allSegments.includes("cpc-upload"),
    },
    {
      id: "d4-medical",
      displayName: "D4 Medical Report",
      required: allSegments.includes("d4-medical-report-upload"),
    },
    {
      id: "cv",
      displayName: "CV",
      required: allSegments.includes("cv-upload"),
    },
  ].filter((doc) => doc.required)

  const StatusBadge = ({ status }: { status: string }) => {
    const statusColors = {
      Verified: "bg-green-100 text-green-800 border-green-200",
      Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Rejected: "bg-red-100 text-red-800 border-red-200",
      "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
      "Not Submitted":
        "bg-palette-jungleGreen100 text-palette-jungleGreen800 border-palette-jungleGreen200",
    }

    return (
      <Badge
        className={
          "pointer-events-none " +
          (statusColors[status as keyof typeof statusColors] ||
            "bg-palette-jungleGreen100 text-palette-jungleGreen800")
        }
      >
        {status}
      </Badge>
    )
  }

  return (
    <Card className="shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">Documents</CardTitle>
              </div>
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {allDocuments.map((requiredDoc) => {
                // Find if this document exists in the provided documents array
                const existingDoc = documents.find((doc) => doc.type === requiredDoc.id)
                const isUploaded = !!existingDoc

                return (
                  <div
                    key={requiredDoc.id}
                    className={`flex justify-between items-center p-3 border rounded-md ${
                      isUploaded ? "bg-green-50" : "bg-palette-jungleGreen000"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-palette-jungleGreen500" />
                      <span className="font-medium">{requiredDoc.displayName}</span>
                      {isUploaded ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-palette-jungleGreen400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isUploaded ? (
                        <>
                          <StatusBadge status={existingDoc.status} />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewDocument(existingDoc)}
                          >
                            View
                          </Button>
                        </>
                      ) : (
                        <Badge className="bg-palette-jungleGreen100 text-palette-jungleGreen800 border-palette-jungleGreen200 pointer-events-none">
                          Not Uploaded
                        </Badge>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export default DocumentsList
